import { FC, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import O2AppUI from '@o2/ui/O2AppUI';
import Header, { LayoutHeaderProps } from './LayoutHeader';
import Body, { LayoutBodyProps } from './LayoutBody';
import Content, { LayoutContentProps } from './LayoutContent';
import SideBar, { LayoutSideBarProps } from './LayoutSidebar';
import FooterBar, { LayoutFooterBarProps } from './LayoutFooterBar';
import { useLayoutUtilityClasses } from './layoutClasses';

export interface LayoutProps {
  children: ReactNode[];
  'data-elm'?: string;
}

type LayoutComponent = FC<LayoutProps> & {
  /**
   * Layout header section
   */
  Header: FC<LayoutHeaderProps>;

  /**
   * Layout body section
   */
  Body: FC<LayoutBodyProps>;

  /**
   * Layout page content section. Must be inside Layout.Body
   */
  Content: FC<LayoutContentProps>;

  /**
   * Layout sidebar. Must be inside Layout.Body.
   */
  SideBar: FC<LayoutSideBarProps>;

  /**
   * Layout footer section (under Layout.Body). For footer inside page content use "footer" prop in Layout.Content.
   */
  FooterBar: FC<LayoutFooterBarProps>;
};

const LayoutRoot = styled('div', {
  name: 'Layout',
  slot: 'Root',
})(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
  alignItems: 'stretch',
}));

const Layout: LayoutComponent = ({ children, 'data-elm': dataElm }) => {
  const utilityClasses = useLayoutUtilityClasses();

  return (
    <O2AppUI>
      <LayoutRoot className={utilityClasses.root} data-elm={dataElm}>
        {children}
      </LayoutRoot>
    </O2AppUI>
  );
};

Layout.Header = Header;
Layout.Body = Body;
Layout.Content = Content;
Layout.SideBar = SideBar;
Layout.FooterBar = FooterBar;

export default Layout;
